/**
 * PaletteDebugPage · v2.7.40 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Read-only developer panel that renders all 14 NWS hazard chips
 * side-by-side with the current hex from `nwsColors.js` AND the
 * federal-spec hex, highlighting any drift visually. Locks down
 * regressions after a palette refactor.
 *
 * Route: /admin/developer/palette-debug
 *
 * "Reset to federal default" copies the canonical federal hex table
 * to the clipboard as a JSON snippet ready to paste back into
 * `nwsColors.js` if drift is ever detected.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Palette, CheckCircle2, AlertTriangle, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { NWS_INFO } from "@/lib/nwsColors";

// ─────────────────────────────────────────────────────────────────────
// FEDERAL SPEC · NWS_HAZARD_PALETTES — these 5 entries are mandated by
// the federal NWS spec the user has reiterated across sessions. The
// remaining 9 documented hazards do not have a federal-spec equivalent,
// so their canonical = current code value (the test row simply mirrors).
// ─────────────────────────────────────────────────────────────────────
const FEDERAL_SPEC = {
  "Tornado Warning":             { hex: "#FF0000", priority: 2,  rgb: [255, 0,   0]   },
  "Severe Thunderstorm Warning": { hex: "#FFA500", priority: 4,  rgb: [255, 165, 0]   },
  "Flash Flood Warning":         { hex: "#00FF00", priority: 5,  rgb: [0,   255, 0]   },
  "Tornado Watch":               { hex: "#FFFF00", priority: 45, rgb: [255, 255, 0]   },
  "Severe Thunderstorm Watch":   { hex: "#DB7093", priority: 46, rgb: [219, 112, 147] },
};

// All hazards we surface to operators. The query strings here are the
// exact match keys NWS_INFO() looks for via .includes().
const HAZARDS = [
  // Emergencies
  { event: "Tornado Emergency",          family: "Emergency" },
  { event: "Flash Flood Emergency",      family: "Emergency" },
  // Warnings
  { event: "Tornado Warning",            family: "Warning"   },
  { event: "Severe Thunderstorm Warning", family: "Warning"  },
  { event: "Flash Flood Warning",        family: "Warning"   },
  { event: "Flood Warning",              family: "Warning"   },
  { event: "Hurricane Warning",          family: "Warning"   },
  { event: "Blizzard Warning",           family: "Warning"   },
  { event: "Ice Storm Warning",          family: "Warning"   },
  { event: "Winter Storm Warning",       family: "Warning"   },
  // Watches
  { event: "Tornado Watch",              family: "Watch"     },
  { event: "Severe Thunderstorm Watch",  family: "Watch"     },
  { event: "Flood Watch",                family: "Watch"     },
  // Statements
  { event: "Special Weather Statement",  family: "Statement" },
];

export default function PaletteDebugPage() {
  // Build the comparison rows — pure derive from NWS_INFO + FEDERAL_SPEC
  const rows = useMemo(() => HAZARDS.map(({ event, family }) => {
    const info = NWS_INFO(event);
    const spec = FEDERAL_SPEC[event];
    return {
      event,
      family,
      currentHex: info.stroke.toUpperCase(),
      specHex: spec ? spec.hex.toUpperCase() : null,
      priority: spec?.priority ?? info.priority ?? null,
      drift: spec ? spec.hex.toUpperCase() !== info.stroke.toUpperCase() : false,
      kind: info.kind,
    };
  }), []);

  const driftCount = rows.filter((r) => r.drift).length;
  const auditedCount = rows.filter((r) => r.specHex).length;

  const copyFederalSpec = () => {
    const snippet = JSON.stringify({ NWS_HAZARD_PALETTES: FEDERAL_SPEC }, null, 2);
    navigator.clipboard?.writeText(snippet);
    toast.success("Federal-spec JSON copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="palette-debug-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="back-to-admin">
            <ArrowLeft className="h-4 w-4"/>Admin Console
          </Link>
          <div className="h-6 w-px bg-zinc-800"/>
          <Palette className="h-6 w-6 text-fuchsia-300"/>
          <h1 className="text-2xl font-extrabold tracking-tight">NWS Palette Debug</h1>
          {driftCount === 0 ? (
            <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-400/40 text-[10px] uppercase tracking-widest" data-testid="palette-overall-status">
              <CheckCircle2 className="h-3 w-3 mr-1 inline"/>Federal spec aligned · {auditedCount}/{auditedCount} match
            </Badge>
          ) : (
            <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/50 text-[10px] uppercase tracking-widest" data-testid="palette-overall-status">
              <AlertTriangle className="h-3 w-3 mr-1 inline"/>{driftCount} hex drift{driftCount === 1 ? "" : "s"} detected
            </Badge>
          )}
          <Button onClick={copyFederalSpec} variant="ghost" className="ml-auto text-fuchsia-300 hover:text-fuchsia-200 text-[10px] uppercase tracking-widest" data-testid="copy-federal-spec-btn">
            <Copy className="h-3.5 w-3.5 mr-1.5"/>Reset to federal default
          </Button>
        </div>

        {/* Description */}
        <p className="text-xs text-zinc-400 leading-relaxed max-w-3xl">
          Every NWS hazard chip below renders with both the current frontend hex (from <code className="text-fuchsia-300">@/lib/nwsColors.js</code>) and the federal <code className="text-fuchsia-300">NWS_HAZARD_PALETTES</code> spec hex side-by-side. The 5 spec-mandated rows are audited; the remaining 9 are documented-but-non-spec and only display the current code value. If you ever see a <span className="text-rose-300 font-bold">DRIFT</span> badge, click <span className="text-fuchsia-300 font-bold">Reset to federal default</span> to copy the canonical JSON, then paste it into <code className="text-fuchsia-300">nwsColors.js</code> and commit.
        </p>

        {/* The grid · group by family */}
        {["Emergency", "Warning", "Watch", "Statement"].map((family) => {
          const familyRows = rows.filter((r) => r.family === family);
          const PLURAL = { Emergency: "Emergencies", Warning: "Warnings", Watch: "Watches", Statement: "Statements" };
          return (
          <Card key={family} className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold px-4 py-2 border-b border-fuchsia-400/40 bg-zinc-950">
              {PLURAL[family]} ({familyRows.length})
            </div>
            <div className="divide-y divide-zinc-800">
              {familyRows.map((r) => (
                <PaletteRow key={r.event} row={r}/>
              ))}
            </div>
          </Card>
          );
        })}

        {/* Reference link */}
        <p className="text-[10px] text-zinc-500 italic">
          Authoritative source ·{" "}
          <a href="https://www.weather.gov/help-map" target="_blank" rel="noreferrer" className="text-fuchsia-300 hover:text-fuchsia-200 underline">
            weather.gov/help-map <ExternalLink className="h-2.5 w-2.5 inline"/>
          </a>
          {" "}· Last reconciled v2.7.40 · 2026-02-21
        </p>
      </div>
    </div>
  );
}

function PaletteRow({ row }) {
  return (
    <div
      className="grid grid-cols-12 items-center px-4 py-3 text-xs gap-3"
      data-testid={`palette-row-${row.event.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Live chip — colored by current hex */}
      <div className="col-span-1">
        <span
          className="inline-block w-7 h-7 rounded border border-zinc-700 shadow"
          style={{ background: row.currentHex }}
          data-testid={`palette-chip-${row.event.toLowerCase().replace(/\s+/g, "-")}`}
        />
      </div>
      {/* Event name */}
      <div className="col-span-4">
        <div className="text-zinc-100 font-bold">{row.event}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{row.kind}{row.priority != null ? ` · priority ${row.priority}` : ""}</div>
      </div>
      {/* Current hex */}
      <div className="col-span-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">Current code</div>
        <code className="text-zinc-100 font-mono">{row.currentHex}</code>
      </div>
      {/* Federal spec hex */}
      <div className="col-span-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">Federal spec</div>
        <code className={`font-mono ${row.specHex ? "text-zinc-100" : "text-zinc-600"}`}>{row.specHex || "—"}</code>
      </div>
      {/* Status */}
      <div className="col-span-1 text-right">
        {row.specHex == null ? (
          <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[9px] uppercase">non-spec</Badge>
        ) : row.drift ? (
          <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/50 text-[9px] uppercase font-bold" data-testid={`palette-drift-${row.event.toLowerCase().replace(/\s+/g, "-")}`}>
            <AlertTriangle className="h-2.5 w-2.5 mr-0.5 inline"/>drift
          </Badge>
        ) : (
          <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-400/40 text-[9px] uppercase font-bold" data-testid={`palette-match-${row.event.toLowerCase().replace(/\s+/g, "-")}`}>
            <CheckCircle2 className="h-2.5 w-2.5 mr-0.5 inline"/>match
          </Badge>
        )}
      </div>
    </div>
  );
}
